import { PreviousYearsAudit } from '@/api/fund/types';
import { FundStructure } from '@/pages/setup-fund/constants/fund-structure';
import { FundInvestmentCompaniesActStatus } from '@/pages/setup-fund/constants/investment-companies-act-status';
import { AccountingType } from '@/stores/slices/setup-fund-slice';

export interface AnnualNetReturn {
  year: number;
  return: number;
}

export interface ReportedNetReturn {
  /** Reported NAV date, ISO `YYYY-MM-DD`. */
  date: string;
  /** Cumulative net return since inception at this date, as a fraction. */
  return: number;
}

export const NavDataVerificationStatus = {
  NotConfirmed: 'NotConfirmed',
  VerifiedByGamma: 'VerifiedByGamma',
  ExpiredVerification: 'ExpiredVerification',
  SourcedFromFundAdmin: 'SourcedFromFundAdmin',
} as const;

export type NavDataVerificationStatus =
  (typeof NavDataVerificationStatus)[keyof typeof NavDataVerificationStatus];

export interface NavDataVerificationView {
  status: NavDataVerificationStatus;
  verifiedAt: string | null;
  fundAdminName: string | null;
}

export interface PerformanceSummary {
  annualizedReturnSinceInception: number | null;
  annualizedReturnSinceInceptionDate: string | null;
  lastYearReturn: number | null;
  lastYearReturnYear: number | null;
  maxDrawdown: number | null;
  cumulativeReturnSinceInception: number | null;
  cumulativeReturnSinceInceptionDate: string | null;
  inceptionDate: string | null;
  annualNetReturns: AnnualNetReturn[];
  /**
   * Cumulative net return at every reported NAV date, at the fund's actual
   * reporting cadence (monthly / quarterly / annual). Drives the granular
   * Performance-summary line (GP-3692). Optional for backward compatibility with
   * payloads produced before the field existed. Ordered ascending by date.
   */
  netReturnSeries?: ReportedNetReturn[];
  navDataVerification?: NavDataVerificationView | null;
}

export interface PublicInvestor {
  id: string;
  name: string;
  imageUrl: string | null;
  investedAt: string | null;
}

export interface FeederInvestorEligibility {
  allowed: string[] | null;
  partiallyRestricted: PartialRestriction[] | null;
}

export interface FundFeeder {
  id: string;
  name: string | null;
  legalStructure: string;
  jurisdictionCountry: string;
  jurisdictionState: string | null;
  assignedCountries: string[];
  offeringDocuments: Documents | null;
  subscriptionDocuments: Documents | null;
  riskDisclosureDocuments: Documents | null;
  sharesStructure: SharesStructure | null;
  subscriptionOptions: SubscriptionOptions | null;
  investorEligibility: FeederInvestorEligibility | null;
  investmentCompaniesActStatus: FundInvestmentCompaniesActStatus | null;
  previousYearsAudits: PreviousYearsAudit[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface Fund {
  fundApprovalStatus: string | null;
  id: string;
  entityId: string;
  name: string;
  logoId: string;
  entityLogoUrl: string;
  status: string;
  hasDraftChanges: boolean;
  draftStatus?: string | null;
  details?: FundDetails;
  detailsDraft?: FundDetails & { status?: string | null };
  fundStructure: FundStructure;
  feeders: FundFeeder[];
  feedersDraft: FundFeeder[];
  participants: Participants[];
  description: string;
  investmentDescription: InvestmentDescription;
  returnProfile: ReturnProfile;
  feesData: FeesData;
  minimumInvestment: MinimumInvestment;
  duration: Duration;
  distributionPeriod: DistributionPeriod;
  reportingPeriod: ReportingPeriod;
  investorEligibility: InvestorEligibility;
  offeringDocuments: Documents | null;
  subscriptionDocuments: Documents | null;
  riskDisclosureDocuments: Documents | null;
  marketingDocuments: Documents | null;
  previousYearsAudits: PreviousYearsAudit[] | null;
  sharesStructure: SharesStructure;
  investmentAvailabilityStatus: string;
  updatedAt: string;
  entityName: string;
  coverImageId: string;
  cardImageId: string | null;
  countrySpecificExemptions: CountryExemption[];
  redemption: Redemption;
  subscriptionOptions?: SubscriptionOptions | null;
  documensoTemplateId: string | null;
  members: Members[];
  directors: Members[];
  transferability: string | null;
  accountingType: AccountingType | null;
  investmentCompaniesActStatus: FundInvestmentCompaniesActStatus | null;
  performanceSummary: PerformanceSummary | null;
  howFundWorks: HowFundWorks | null;
  fundBenefits: FundBenefit[] | null;
  fundPresentation: FundPresentation | null;
  fundTargetInvestor: FundTargetInvestor | null;
  managerAlignment: ManagerAlignment | null;
  faq: FundFaq[] | null;
  // TODO: backend not implemented — fields remain undefined until backend populates them.
  publicInvestors?: PublicInvestor[];
  hasCurrentUserInvested?: boolean;
  hasRequestedDdPack: boolean;
}

export type FundDetails = Omit<
  Fund,
  | 'id'
  | 'entityId'
  | 'name'
  | 'entityLogoUrl'
  | 'entityName'
  | 'fundApprovalStatus'
  | 'status'
  | 'hasDraftChanges'
  | 'draftStatus'
  | 'details'
  | 'detailsDraft'
  | 'updatedAt'
  | 'members'
  | 'directors'
  | 'transferability'
  | 'fundStructure'
  | 'feeders'
  | 'feedersDraft'
> & {
  transferability?: string | null;
};

export interface InvestmentDescription {
  description: string;
  category: string;
  assets: string[];
  stage: string[];
  sizeInMillionsUsd: number;
  aumInMillionsUsd: number;
  strategies: string[];
  website: string;
  legalStructure: string;
  jurisdictionCountry: string;
  jurisdictionState: string;
  jurisdictionCountryName: string;
}

export interface ReturnProfile {
  targetReturnRate: number;
  feeType: string;
  projectedReturnProfile: string;
  yearlyReturns: YearlyReturn[];
  strikeFrequency: 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly' | 'Annually' | null;
  preferredDataSource: 'UploadedFile' | 'ThisForm' | 'Merge' | null;
}

export interface YearlyReturn {
  year: number;
  returnPercentage: number;
  maxDrawdown: number;
  discountPercentage: number;
}

export interface FeesData {
  description: string;
  fees: Fee[];
}

export interface Fee {
  feeType: string;
  percentage: number;
  type: string;
  frequency: string;
  notes: string;
}

export interface MinimumInvestment {
  currency: string;
  descriptionMinimumInvestment: string;
  descriptionSecondaryMinimumInvestment: string;
  valueMinimumInvestment: number;
  valueSecondaryInvestment: number;
  notesMinimumInvestment: string;
  notesSecondaryMinimumInvestment: string;
  notApplicableMinimumInvestment: boolean;
  notApplicableSecondaryMinimumInvestment: boolean;
  additionalDescription: string;
}

export interface Duration {
  startDate: string; // ISO date string
  durationInYears: number;
  isOpenEnded: boolean;
  isNewFund: boolean;
  additionalInfo: string;
}

export interface DistributionPeriod {
  frequency: number;
  period: string;
  frequencyNotApplicable: boolean;
  additionalInfo: string;
  type: string;
}

export interface ReportingPeriod {
  period: string;
  reportingNotApplicable: boolean;
  additionalInfo: string;
}

export interface InvestorEligibility {
  filter: string;
  allowed: string[];
  notAllowed: string[];
  partiallyRestricted: PartialRestriction[];
  unassigned?: string[] | null;
}

export interface PartialRestriction {
  country: string;
  states: string[];
}

export interface Documents {
  notes: string;
  documentIds: string[];
}

export interface SharesStructure {
  shares: Share[];
}

export interface Share {
  type: string;
  numberOfShares: number;
  shareholders: Shareholder[];
}

export interface Shareholder {
  type: string;
  fullName: string;
  email: string;
  numberOfShares: number;
  percentage: number;
}

export interface CountryExemption {
  country: string;
  exemptions: string[];
}

export interface Redemption {
  lockUpPeriod: string[];
  frequency: string;
  priority: string;
  noticeInDays: number;
  conditions: string;
  gate: Gate | null;
  noRedemptionAllowed: boolean;
}

export interface Gate {
  max: number;
  gates: number;
  months: number;
}

export interface SubscriptionOptions {
  subscriptionMethod: string;
  wireInformation: WireInformation | null;
}

export interface WireInformation {
  bankDetails: BankDetails;
  beneficiaryDetails: BeneficiaryDetails;
}

export interface BankDetails {
  name: string;
  address: string;
  city: string;
  country: string;
  state: string;
  zipCode: string;
}

export interface BeneficiaryDetails {
  name: string;
  address: string;
  accountNumber: string;
  bankIdentifierCode: string;
  city: string;
  country: string;
  state: string;
  zipCode: string;
  notes: string;
  ABARouting: string;
}

export interface Participants {
  id: string;
  name: string;
  role: string;
  logoImageId: string;
  logoImageUrl: string;
  website?: string | null;
}

export interface Members {
  id: string;
  userId: string;
  title: string;
  name: string | null;
  imageUrl: string | null;
  description: string | null;
  company: string | null;
  jobTitle: string | null;
  linkedinProfile: string | null;
  xProfile: string | null;
  nationalities: string[] | null;
  createdAt: string;
  updatedAt: string;
  alias: string | null;
}

export interface HowFundWorks {
  whatItInvestsIn: string | null;
  howItAimsToMakeMoney: string | null;
  whatCanGoWrong: string | null;
}

export interface FundBenefit {
  title: string | null;
  description: string | null;
}

export const FundPresentationType = {
  VideoLink: 'VideoLink',
  UploadDeck: 'UploadDeck',
} as const;

export type FundPresentationType = (typeof FundPresentationType)[keyof typeof FundPresentationType];

export interface FundPresentation {
  presentationType: FundPresentationType | null;
  videoUrl: string | null;
  deckFileId: string | null;
}

export interface FundTargetInvestor {
  idealInvestorProfile: string[];
  whoItsNotFor: string[];
}

export interface ManagerAlignment {
  gpCapitalInvested: number | null;
  alignmentDescription: string | null;
}

export interface FundFaq {
  question: string | null;
  answer: string | null;
}
